import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import TwoFactorSettings from "./TwoFactorSettings";
import { changePassword, getMe, updateProfile } from "../api/authApi";
import { setMe } from "../redux/sessionSlice";
import { useTranslation } from "react-i18next";

const errText = (e, t) => e?.response?.data?.detail || t("profile.error");

const card = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--shadow-sm)", padding: 20, display: "grid", gap: 14, alignContent: "start" };
const cardTitle = { margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text-primary)" };
const labelStyle = { display: "grid", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--text-label)" };
const inputStyle = { padding: "9px 11px", border: "1px solid var(--border-strong)", borderRadius: 6, background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 14, fontWeight: 400 };
const readonlyStyle = { ...inputStyle, background: "var(--bg-subtle)", color: "var(--text-muted)" };
const primaryBtn = { justifySelf: "start", padding: "9px 18px", border: "none", borderRadius: 6, background: "var(--btn-primary)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const badge = { display: "inline-block", padding: "2px 10px", borderRadius: 999, background: "var(--info-bg)", border: "1px solid var(--info-border)", color: "var(--info-text)", fontSize: 12, fontWeight: 600 };

export default function Profile() {
  const { t } = useTranslation();
  const me = useSelector((s) => s.session?.me);
  const accessToken = useSelector((s) => s.session?.accessToken);
  const dispatch = useDispatch();
  const [name, setName] = useState(me?.full_name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_new_password: "" });
  const [busy, setBusy] = useState(false);

  const saveName = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile(accessToken, { full_name: name });
      dispatch(setMe(await getMe(accessToken)));
      toast.success(t("profile.name.saved"));
    } catch (err) { toast.error(errText(err, t)); } finally { setBusy(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await changePassword(accessToken, pw);
      setPw({ current_password: "", new_password: "", confirm_new_password: "" });
      toast.success(t("profile.password.changed"));
    } catch (err) { toast.error(errText(err, t)); } finally { setBusy(false); }
  };

  const field = (key, label) => (
    <label style={labelStyle}>{label}
      <input style={inputStyle} type="password" autoComplete={key === "current_password" ? "current-password" : "new-password"}
        value={pw[key]} onChange={(e) => setPw({ ...pw, [key]: e.target.value })} />
    </label>
  );
  const initial = (me?.full_name || me?.email || "U").charAt(0).toUpperCase();
  const role = me?.tenant_role || me?.global_role;

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <h1 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--text-muted)" }}>{t("profile.title")}</h1>
      <div style={{ ...card, gridTemplateColumns: "auto 1fr", alignItems: "center", gap: 18, marginBottom: 20 }}>
        <div aria-hidden="true" style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--avatar-bg)", color: "var(--avatar-color)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700 }}>{initial}</div>
        <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 22, color: "var(--text-primary)", overflowWrap: "anywhere" }}>{me?.full_name || me?.email || t("profile.title")}</h2>
          <span style={{ color: "var(--text-muted)", fontSize: 14, overflowWrap: "anywhere" }}>{me?.email ?? "-"}</span>
          <span style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
            {role && <span style={badge}>{role}</span>}
            {me?.tenant_name && <span style={badge}>{me.tenant_name}</span>}
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
        <form style={card} onSubmit={saveName}>
          <h3 style={cardTitle}>{t("profile.info.title")}</h3>
          <label style={labelStyle}>{t("profile.name.title")}
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoComplete="name" />
          </label>
          <label style={labelStyle}>{t("profile.email")}
            <input style={readonlyStyle} value={me?.email ?? ""} readOnly />
          </label>
          <label style={labelStyle}>{t("profile.role")}
            <input style={readonlyStyle} value={role || ""} readOnly />
          </label>
          <button style={{ ...primaryBtn, opacity: busy || !name.trim() ? 0.6 : 1 }} type="submit" disabled={busy || !name.trim()}>{t("common.save")}</button>
        </form>

        <form style={card} onSubmit={savePassword}>
          <h3 style={cardTitle}>{t("profile.password.title")}</h3>
          {field("current_password", t("profile.password.current"))}
          {field("new_password", t("profile.password.new"))}
          {field("confirm_new_password", t("profile.password.confirm"))}
          <button style={{ ...primaryBtn, opacity: busy || !pw.current_password || !pw.new_password ? 0.6 : 1 }} type="submit" disabled={busy || !pw.current_password || !pw.new_password}>{t("profile.password.submit")}</button>
        </form>

        <div style={{ ...card, gridColumn: "1 / -1" }}>
          <TwoFactorSettings embedded />
        </div>
      </div>
    </div>
  );
}
